/**
 * Client-side computation of the per-stage calculation footer.
 *
 * Given a list of calculations and the items CURRENTLY VISIBLE in a stage,
 * produce the display lines for that stage's footer. Everything is computed
 * over the already-loaded board items so the footer always matches what's on
 * screen and respects filters/search automatically (no aggregate API call).
 */

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  formatCurrencyValue,
  type NumericValue,
  sumQuantityValues,
} from '@/lib/crm/list/utils/aggregationTransformers';
import type { PropertyValue, QuantityValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { StageFooterCalculation } from './stageFooterConfig';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal item shape needed to compute the footer (a CRM object). */
export interface StageFooterItem {
  properties?: Record<string, PropertyValue>;
}

export interface StageFooterLine {
  /** Stable per-line React key. */
  key: string;
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Formatters (module-level for stable references)
// ---------------------------------------------------------------------------

const COUNT_FORMATTER = new Intl.NumberFormat('en-US');
const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coerce a raw property value to a finite number, or null if not numeric. */
function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Whether a raw value looks like a currency/quantity `{ amount, unit }`. */
function isQuantityValue(raw: unknown): raw is QuantityValue {
  return typeof raw === 'object' && raw !== null && 'amount' in raw;
}

// ---------------------------------------------------------------------------
// Compute
// ---------------------------------------------------------------------------

export function computeStageFooterLines(
  calculations: readonly StageFooterCalculation[],
  items: readonly StageFooterItem[],
  propertySchemas: Record<string, PropertyTypeSchema>
): StageFooterLine[] {
  const lines: StageFooterLine[] = [];

  calculations.forEach((calc, index) => {
    const key = `${calc.function}:${calc.property ?? ''}:${index}`;

    if (calc.function === 'count') {
      lines.push({
        key,
        label: 'Count',
        value: COUNT_FORMATTER.format(items.length),
      });
      return;
    }

    // sum / avg require a property that exists in the schema.
    const propName = calc.property;
    if (!propName) {
      return;
    }
    const schema = propertySchemas[propName];
    if (!schema) {
      return;
    }
    const propLabel = schema.label || propName;
    const verb = calc.function === 'sum' ? 'Sum' : 'Avg';
    const label = `${verb} · ${propLabel}`;

    if (schema.field_type_name === 'currency') {
      const quantities: QuantityValue[] = [];
      for (const item of items) {
        const raw = getPropertyValue<unknown>(item.properties?.[propName]);
        if (isQuantityValue(raw)) {
          quantities.push(raw);
        }
      }

      if (calc.function === 'sum') {
        lines.push({
          key,
          label,
          value: formatCurrencyValue(sumQuantityValues(quantities)),
        });
        return;
      }

      // avg: per-unit average (sum / count within each unit bucket).
      if (quantities.length === 0) {
        lines.push({ key, label, value: '—' });
        return;
      }
      const countsByUnit = new Map<string | null, number>();
      for (const q of quantities) {
        countsByUnit.set(q.unit, (countsByUnit.get(q.unit) ?? 0) + 1);
      }
      const averaged: NumericValue[] = sumQuantityValues(quantities).map(nv => {
        const count = countsByUnit.get(nv.unit) ?? 0;
        return {
          amount: count > 0 ? nv.amount / count : nv.amount,
          unit: nv.unit,
        };
      });
      lines.push({ key, label, value: formatCurrencyValue(averaged) });
      return;
    }

    // Numeric (number / percentage / other plain-numeric field types).
    let sum = 0;
    let count = 0;
    for (const item of items) {
      const raw = getPropertyValue<unknown>(item.properties?.[propName]);
      const num = toNumber(raw);
      if (num !== null) {
        sum += num;
        count += 1;
      }
    }

    if (calc.function === 'sum') {
      lines.push({ key, label, value: NUMBER_FORMATTER.format(sum) });
      return;
    }
    lines.push({
      key,
      label,
      value: count === 0 ? '—' : NUMBER_FORMATTER.format(sum / count),
    });
  });

  return lines;
}
